import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Lang } from '@/i18n';
import { CarouselChecker } from '@/components/tools/CarouselChecker';
import { HookAnalyzer } from '@/components/tools/HookAnalyzer';
import { DiagnosticWizard } from '@/components/tools/DiagnosticWizard';
import { LikesCalculator } from '@/components/tools/LikesCalculator';
import { PostFormatter } from '@/components/tools/PostFormatter';
import { PostIdeas } from '@/components/tools/PostIdeas';
import { PostSimulator } from '@/components/tools/PostSimulator';
import { ShadowbanWizard } from '@/components/tools/ShadowbanWizard';
import { VideoChecker } from '@/components/tools/VideoChecker';

/**
 * Le registre des outils gratuits — une entrée par outil, sur le modèle des
 * articles de blog (`lib/blog.ts`) : chaque outil vit dans SA langue, pas
 * traduit automatiquement. `description` porte des liens internes en JSX
 * (d'où l'extension `.tsx`) ; `faq` reste du texte brut, parce qu'elle se
 * mirrore telle quelle dans le JSON-LD `FAQPage` (voir `lib/seo.ts`).
 */

export type ToolArchetype = 'wizard' | 'calculator' | 'analyzer' | 'directory';

export interface ToolFaqItem {
  q: string;
  a: string;
}

export interface ToolEntry {
  slug: string;
  archetype: ToolArchetype;
  lang: Lang;
  /** Balise `<title>` et carte de l'index. */
  title: string;
  /** Titre H1, grand et centré, en haut de la page outil. */
  h1: string;
  /** Une phrase : ce que le visiteur repart avec. */
  valueLine: string;
  metaDescription: string;
  /**
   * H2 au-dessus de la description, en question : c'est le titre que les
   * moteurs (et les réponses IA) rattachent au bloc méthode qui le suit.
   */
  descriptionTitle: string;
  description: ReactNode[];
  faq: ToolFaqItem[];
  Widget: ComponentType;
}

const TOOLS: ToolEntry[] = [
  {
    slug: 'pourquoi-post-linkedin-pas-de-vues',
    archetype: 'wizard',
    lang: 'fr',
    title: "Diagnostic : pourquoi mon post LinkedIn n'a pas de vues",
    h1: "Pourquoi votre post LinkedIn n'a pas de vues",
    valueLine:
      'Répondez à 4 questions sur votre post : la cause la plus probable en 30 secondes, sans compte ni mot de passe.',
    metaDescription:
      "Votre post LinkedIn n'a pas de vues ? Répondez à 4 questions (format, horaire, taille de réseau, réactions à l'heure) pour un diagnostic précis et une action concrète pour le prochain post.",
    descriptionTitle: 'Comment le diagnostic trouve-t-il la cause ?',
    description: [
      <>
        Un post à zéro vue n'est pas forcément un post raté : c'est souvent un post
        que{' '}
        <Link to="/blog/post-linkedin-sans-engagement?lang=fr">presque personne n'a vu</Link>, ce qui n'a rien à voir. Ce
        diagnostic pose les questions qui distinguent les deux cas au lieu de deviner.
      </>,
      <>
        La logique vient de la façon dont LinkedIn diffuse un post : un petit échantillon d'abord, une diffusion plus
        large seulement si ce groupe réagit dans l'heure. Nous avons détaillé ce mécanisme dans{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure d'un post LinkedIn</Link> et dans notre étude
        sur <Link to="/blog/meilleur-moment-pour-poster-sur-linkedin?lang=fr">le meilleur moment pour poster</Link> ; ce sont
        les mêmes mécanismes que l'outil applique à votre cas précis.
      </>,
      <>
        Exemple concret : un carrousel publié un samedi à 21h avec zéro réaction en une heure ? L'outil pointera un
        échantillon de test trop réduit à cet horaire, pas votre contenu. Republier au bon moment change la donne
        bien plus qu'une réécriture. À l'inverse, un post publié un mardi à midi avec zéro réaction pointe vers autre
        chose : l'échantillon était là, il n'a pas réagi.
      </>,
      <>
        Pour aller plus loin sur la mécanique de portée, voir aussi{' '}
        <Link to="/blog/taux-engagement-linkedin?lang=fr">le calcul du taux d'engagement LinkedIn</Link>, qui permet de savoir
        si un post qui a bien démarré tient ensuite la comparaison.
      </>,
    ],
    faq: [
      {
        q: 'Est-ce que 0 vue veut dire que mon post ne plaît pas ?',
        a: "Pas nécessairement. LinkedIn ne montre un post qu'à un petit échantillon de votre réseau au début ; si cet échantillon ne réagit pas dans l'heure, la diffusion s'arrête là. Ce n'est pas un jugement sur le contenu, c'est un test sur un groupe réduit de personnes.",
      },
      {
        q: 'Quel est le meilleur moment pour republier ?',
        a: "Selon l'étude Sprout Social portant sur 307 000 profils, le créneau le plus large est mardi à jeudi, 11h-17h, à l'heure locale de votre audience. Le week-end est systématiquement le plus faible.",
      },
      {
        q: 'Le format de mon post (texte, image, carrousel, vidéo) joue-t-il un rôle ?',
        a: "Le format influence le temps que les gens passent sur le post (ce que LinkedIn appelle le dwell time), mais il ne remplace pas un signal social dans la première heure : un carrousel vu sans réaction s'arrête comme un texte vu sans réaction.",
      },
      {
        q: 'Supprimer et republier mon post peut-il aider ?',
        a: "Rarement. Vous perdez le signal déjà accumulé sur la première version, et la republication est testée exactement de la même façon sur un nouvel échantillon.",
      },
      {
        q: 'Ce diagnostic remplace-t-il une vraie mesure de portée ?',
        a: "Non. C'est une estimation basée sur des mécanismes documentés, pas un accès à vos statistiques LinkedIn réelles. Pour un chiffre exact, regardez les impressions dans les analytics de votre post.",
      },
    ],
    Widget: DiagnosticWizard,
  },
  {
    slug: 'simulateur-post-linkedin',
    archetype: 'analyzer',
    lang: 'fr',
    title: 'Simulateur de post LinkedIn : aperçu « voir plus » et contrôle avant publication',
    h1: 'Simulateur de post LinkedIn',
    valueLine:
      'Collez votre brouillon : voyez ce que le fil affiche avant « voir plus », et corrigez accroche, lien, hashtags et mur de texte avant de publier.',
    metaDescription:
      "Simulateur de post LinkedIn gratuit : aperçu mobile et ordinateur avec coupure « voir plus » estimée, compteur sur 3 000 caractères et contrôle de l'accroche, des liens, des hashtags et des blocs trop longs.",
    descriptionTitle: 'Comment le simulateur contrôle-t-il un post ?',
    description: [
      <>
        Un aperçu seul montre un rendu ; il ne dit pas si le post est bon. Ce simulateur affiche ce que le fil montre
        avant « voir plus » sur mobile et sur ordinateur, puis contrôle le brouillon sur cinq points : longueur,
        accroche, lien, mur de texte et hashtags. Rien n'est envoyé à un serveur : le texte reste dans votre navigateur.
      </>,
      <>
        Une seule limite est officielle : LinkedIn plafonne un post à 3 000 caractères. La position de la coupure « voir
        plus » n'est pas publiée, donc l'outil l'estime en repliant le texte ligne par ligne (environ 3 lignes sur mobile,
        5 sur ordinateur) et le dit. Le seuil de 400 caractères par bloc et celui de 5 hashtags sont des repères éditoriaux,
        pas des règles de la plateforme.
      </>,
      <>
        Exemple : un brouillon dont la première phrase fait environ 130 caractères sans ponctuation est signalé comme coupé
        en plein milieu sur mobile ; la même phrase raccourcie à 60 caractères passe. Un lien dans les premières lignes
        est signalé comme pire emplacement ; en fin de texte, il reste un avertissement, détaillé dans notre guide pour{' '}
        <Link to="/blog/mettre-un-lien-dans-un-post-linkedin?lang=fr">mettre un lien dans un post LinkedIn</Link>.
      </>,
      <>
        Un bon texte ne suffit pas : le post est d'abord testé sur un petit échantillon, d'où l'importance de{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure</Link>. Si un post a déjà été publié sans
        résultat, le{' '}
        <Link to="/tools/pourquoi-post-linkedin-pas-de-vues">diagnostic « pourquoi mon post n'a pas de vues »</Link> cherche la
        cause côté horaire et réseau.
      </>,
    ],
    faq: [
      {
        q: 'Où LinkedIn coupe-t-il un post avec « voir plus » ?',
        a: "LinkedIn ne publie pas cette valeur, et elle dépend de la largeur d'écran, des retours à la ligne et du format du post. Ce simulateur l'estime à environ 3 lignes sur mobile et 5 sur ordinateur ; considérez le résultat comme un ordre de grandeur, pas une mesure.",
      },
      {
        q: "Quelle est la longueur maximale d'un post LinkedIn ?",
        a: "3 000 caractères, selon l'aide officielle de LinkedIn. Au-delà, il faut passer par un article.",
      },
      {
        q: 'Combien de hashtags mettre dans un post ?',
        a: "LinkedIn ne donne pas de chiffre. Notre repère : 2 à 3 hashtags précis, à la fin du texte. L'outil vous prévient au-delà de 5, ou quand un hashtag est au milieu d'une phrase.",
      },
      {
        q: 'Mon texte est-il envoyé quelque part ?',
        a: "Non. L'analyse tourne entièrement dans votre navigateur, rien n'est transmis ni enregistré.",
      },
      {
        q: 'Le simulateur prédit-il la portée de mon post ?',
        a: "Non. Il contrôle la forme du texte, pas son audience. La diffusion dépend surtout des réactions reçues dans la première heure.",
      },
    ],
    Widget: PostSimulator,
  },
  {
    slug: 'combien-de-likes-post-linkedin',
    archetype: 'calculator',
    lang: 'fr',
    title: 'Combien de likes pour un post LinkedIn ? Calculez votre taux et comparez',
    h1: 'Combien de likes pour un post LinkedIn ?',
    valueLine:
      'Saisissez vos chiffres : le taux de votre post, comparé aux médianes de 93 696 posts par format et par taille de réseau.',
    metaDescription:
      "Combien de likes pour un post LinkedIn, c'est normal ? Calculez le taux d'engagement de votre post et situez-le face aux médianes par format (texte, image, vidéo, carrousel) et par nombre d'abonnés.",
    descriptionTitle: 'Comment le taux est-il calculé et comparé ?',
    description: [
      <>
        Le nombre de likes seul ne dit rien : 5 réactions sur 200 impressions, c'est 2,5 %, au-dessus de la médiane des posts texte (1,54 %) ;
        5 réactions sur 5 000 impressions, c'est 0,1 %. Ce calculateur applique la formule de LinkedIn (clics, réactions,
        commentaires et republications divisés par les impressions) puis compare le résultat aux médianes du format et
        du palier d'abonnés. Rien n'est envoyé à un serveur.
      </>,
      <>
        Les repères viennent d'une étude ContentIn sur 93 696 posts, détaillée dans notre guide sur{' '}
        <Link to="/blog/taux-engagement-linkedin?lang=fr">le calcul du taux d'engagement LinkedIn</Link>. Exemple : un
        post image d'un compte de 3 000 abonnés, 1 800 impressions et 73 interactions donne 4,06 %, nettement
        au-dessus de la médiane image (2,94 %). Le seuil de 200 impressions, la bande « dans la norme » de plus ou moins
        25 % et le seuil de portée de 10 % du réseau sont des repères NeverZero, pas des chiffres publiés par LinkedIn.
      </>,
      <>
        Un taux élevé ne veut pas toujours dire que le post a marché : s'il n'a été vu que par vos contacts les plus
        actifs, il est flatteur et la diffusion s'est arrêtée tôt. C'est ce que{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure d'un post LinkedIn</Link> détermine. Si
        votre post a peu de vues, le{' '}
        <Link to="/tools/pourquoi-post-linkedin-pas-de-vues">diagnostic « pourquoi mon post n'a pas de vues »</Link> cherche
        la cause, et le <Link to="/tools/simulateur-post-linkedin">simulateur de post</Link> contrôle l'accroche avant la
        prochaine publication.
      </>,
    ],
    faq: [
      {
        q: 'Combien de likes faut-il pour un bon post LinkedIn ?',
        a: "Il n'y a pas de nombre universel : cela dépend des impressions. Les médianes d'interactions (clics, réactions, commentaires, republications) sont de 5 pour un post texte, 18 pour une image, 16 pour une vidéo et 16 pour un carrousel. Au-dessus de 2,5 % d'engagement sur les impressions, vous faites mieux que la moitié des posts.",
      },
      {
        q: "Quelle est la formule du taux d'engagement LinkedIn ?",
        a: "Taux = (clics + réactions + commentaires + republications) ÷ impressions × 100. C'est la formule qu'applique LinkedIn dans les statistiques d'un post. Exemple : 73 interactions sur 1 800 impressions donnent 4,06 %.",
      },
      {
        q: 'Où trouver les impressions de mon post ?',
        a: "Sous votre post, cliquez sur « Voir les statistiques » : LinkedIn affiche les impressions, les membres atteints et le détail des interactions. Les impressions comptent chaque affichage, les membres atteints seulement les personnes distinctes.",
      },
      {
        q: 'Pourquoi mon taux est élevé alors que mon post a peu de likes ?',
        a: "Parce que le taux est un ratio. 4 réactions sur 50 impressions font 8 %, mais sous 200 impressions le chiffre ne veut plus rien dire : une seule réaction de plus le change de plusieurs points.",
      },
      {
        q: 'Faut-il compter les clics ?',
        a: "Oui, LinkedIn les compte dans les interactions, et les médianes de référence les incluent. Sans eux, votre taux est sous-estimé. C'est pourquoi un post image affiche souvent un taux plus haut qu'un post texte : agrandir l'image est un clic.",
      },
      {
        q: 'Ce calcul est-il envoyé quelque part ?',
        a: "Non. Tout se calcule dans votre navigateur, aucun chiffre n'est transmis ni enregistré.",
      },
    ],
    Widget: LikesCalculator,
  },
  {
    slug: 'mettre-en-gras-post-linkedin',
    archetype: 'analyzer',
    lang: 'fr',
    title: 'Comment mettre en gras sur un post LinkedIn : formateur gratuit avec contrôle',
    h1: 'Mettre en gras dans un post LinkedIn',
    valueLine:
      'Écrivez votre post, sélectionnez les mots à faire ressortir : gras, italique ou gras italique, avec un contrôle des accents, des hashtags et des liens avant de copier.',
    metaDescription:
      "Comment mettre en gras sur un post LinkedIn ? Sélectionnez vos mots, appliquez gras ou italique, et vérifiez ce qui casse (accents, hashtags, liens) avant de copier. Gratuit, sans compte.",
    descriptionTitle: 'Comment fonctionne le gras sur LinkedIn ?',
    description: [
      <>
        LinkedIn n'offre pas de bouton gras dans la zone de rédaction d'un post. Les formateurs comme celui-ci
        remplacent les lettres par leurs équivalents du bloc Unicode des symboles mathématiques : « Bonjour » devient
        « 𝐁𝐨𝐧𝐣𝐨𝐮𝐫 ». Le résultat se colle comme du texte ordinaire, mais ce n'est pas du vrai gras. Rien n'est envoyé à un
        serveur : le texte reste dans votre navigateur.
      </>,
      <>
        Ce bloc a des trous, et c'est là que l'outil travaille. Il n'existe pas de lettre accentuée en gras : « é » est
        reconstitué avec un « e » gras suivi d'un accent séparé, qui peut se décaler selon la police. Les chiffres existent
        en gras (« 2026 » donne « 𝟐𝟎𝟐𝟔 ») mais pas en italique, et « œ » n'a aucune version. L'outil laisse ces caractères
        tels quels et vous le dit au lieu de les cacher.
      </>,
      <>
        Un hashtag en gras n'est plus fait de lettres ordinaires et n'a donc très probablement plus de lien cliquable ;
        une adresse en gras ne mène nulle part. Le contrôle les repère. Les lecteurs d'écran peuvent aussi épeler les lettres
        stylées et la recherche ne les retrouve pas : réservez le style à un titre ou à quelques mots, jamais à un paragraphe.
      </>,
      <>
        La première ligne est celle qui décide du clic sur « voir plus » : voyez où elle est coupée avec le{' '}
        <Link to="/tools/simulateur-post-linkedin">simulateur de post LinkedIn</Link>, et les limites de longueur dans notre
        guide sur la <Link to="/blog/taille-post-linkedin?lang=fr">taille d'un post LinkedIn</Link>. La mise en forme ne remplace
        pas la{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">première heure</Link> : un post bien présenté que personne ne
        commente reste peu diffusé.
      </>,
    ],
    faq: [
      {
        q: 'Peut-on vraiment mettre du texte en gras sur LinkedIn ?',
        a: "Pas avec un réglage de la zone de rédaction. Le gras que l'on voit dans les posts est du texte Unicode copié-collé : chaque lettre est remplacée par une lettre mathématique en gras. Il s'affiche sur la plupart des appareils, mais ce n'est pas un formatage.",
      },
      {
        q: 'Pourquoi mes accents bougent-ils en gras ?',
        a: "Le bloc Unicode du gras ne contient pas de lettres accentuées. Pour « é », l'outil écrit un « e » gras puis un accent combinant. Certaines polices posent cet accent à côté de la lettre. Vérifiez l'aperçu sur votre téléphone avant de publier.",
      },
      {
        q: "Le texte en gras est-il lu par les lecteurs d'écran ?",
        a: "Souvent mal. Beaucoup de lecteurs d'écran lisent ces caractères comme des symboles mathématiques ou les épellent. Limitez le gras à quelques mots et ne l'utilisez pas pour une information indispensable.",
      },
      {
        q: 'Un hashtag ou un lien peut-il être en gras ?',
        a: "Non, c'est à éviter. Un hashtag écrit en lettres mathématiques ne sera très probablement pas reconnu, et un lien en gras ne fonctionne plus. L'outil vous prévient dans les deux cas.",
      },
      {
        q: 'Combien de texte mettre en gras ?',
        a: "Peu. Notre repère, qui n'est pas une règle de LinkedIn : moins d'un tiers du texte, et pas de passage de plus de 80 caractères d'un seul tenant. Un titre et deux ou trois mots clés suffisent.",
      },
      {
        q: 'Mon texte est-il envoyé quelque part ?',
        a: "Non. La conversion tourne entièrement dans votre navigateur, rien n'est transmis ni enregistré.",
      },
    ],
    Widget: PostFormatter,
  },
  {
    slug: 'accroche-post-linkedin',
    archetype: 'analyzer',
    lang: 'fr',
    title: "Analyseur d'accroche LinkedIn : testez vos premières lignes avant « voir plus »",
    h1: "Analyseur d'accroche LinkedIn",
    valueLine:
      "Collez vos premières lignes : voyez ce qui reste avant « voir plus », quels mots affaiblissent l'accroche et par quoi commencer à la place.",
    metaDescription:
      "Analyseur d'accroche LinkedIn gratuit : repérez l'ouverture de politesse, la question fermée, le chiffre caché après « voir plus », et comparez deux versions de votre première phrase. Sans compte.",
    descriptionTitle: "Comment l'analyseur juge-t-il une accroche ?",
    description: [
      <>
        Une accroche est ce que le lecteur voit avant « voir plus », soit environ trois lignes sur mobile. L'analyseur
        estime cette coupure, surligne dans votre texte ce qui sert la première phrase (chiffre, nom propre, contraste) et
        souligne ce qui la dessert, puis passe six contrôles : ouverture de politesse, longueur de la première ligne,
        précision, tension, question fermée et parasites. Rien n'est envoyé à un serveur.
      </>,
      <>
        Aucune de ces règles ne vient de LinkedIn, et aucune ne prédit une portée : ce sont des repères éditoriaux. L'outil
        classe aussi l'accroche (chiffrée, en question, en récit, à contre-pied) parce que chaque type demande autre chose.
        Exemple : « Aujourd'hui je voulais partager avec vous ce que nous avons appris » est repérée comme ouverture de
        politesse, et l'outil propose de commencer par la phrase qui suit dans votre propre texte.
      </>,
      <>
        Le cas qui surprend le plus : un chiffre présent dans le post mais placé après la coupure. Il compte pour le
        lecteur seulement s'il est visible, donc l'outil le signale et vous dit de le remonter. Pour la mécanique de la
        coupure, voir la <Link to="/blog/taille-post-linkedin?lang=fr">taille d'un post LinkedIn</Link> ; pour un contrôle du
        post entier (lien, hashtags, mur de texte), le <Link to="/tools/simulateur-post-linkedin">simulateur de post</Link>.
      </>,
      <>
        Une accroche fait cliquer, elle ne fait pas diffuser : c'est{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure</Link> qui décide de la portée. Et si un post
        déjà publié n'a pas décollé, le{' '}
        <Link to="/tools/pourquoi-post-linkedin-pas-de-vues">diagnostic « pourquoi mon post n'a pas de vues »</Link> cherche la
        cause côté horaire et réseau.
      </>,
    ],
    faq: [
      {
        q: "Qu'est-ce qu'une bonne accroche LinkedIn ?",
        a: "C'est la ou les premières lignes qui donnent au lecteur une raison de cliquer sur « voir plus » : un résultat chiffré, un contraste ou une question ouverte, formulés avec un élément précis. « Nous avons arrêté de poster tous les jours : 3 clients en 6 semaines » tient en une ligne et pose les trois.",
      },
      {
        q: 'Où LinkedIn coupe-t-il un post avec « voir plus » ?',
        a: "LinkedIn ne publie pas cette valeur. Elle dépend de la largeur d'écran, des retours à la ligne et des emojis. L'outil l'estime à environ 3 lignes sur mobile ; prenez le résultat comme un ordre de grandeur.",
      },
      {
        q: "Pourquoi « Aujourd'hui je voulais partager » est-il signalé ?",
        a: "Parce que cette phrase occupe la place la plus visible sans rien dire du sujet. Elle n'est pas interdite, mais le lecteur décide en une seconde s'il déplie, et il n'a rien appris. L'outil vous propose de commencer par la phrase suivante.",
      },
      {
        q: 'Faut-il éviter les questions dans une accroche ?',
        a: "Pas toutes. Une question ouverte (comment, pourquoi, combien) ouvre une boucle. Une question fermée (« Saviez-vous que… ? », « Est-ce que… ? ») se règle par un oui ou un non mental, et l'outil la signale.",
      },
      {
        q: "L'analyseur prédit-il le nombre de vues de mon post ?",
        a: "Non. Il contrôle la forme de l'accroche, pas son audience. Une accroche solide fait cliquer, mais la diffusion dépend surtout des réactions reçues dans la première heure.",
      },
      {
        q: 'Mon texte est-il envoyé quelque part ?',
        a: "Non. L'analyse tourne entièrement dans votre navigateur, rien n'est transmis ni enregistré.",
      },
    ],
    Widget: HookAnalyzer,
  },
  {
    slug: 'shadowban-linkedin',
    archetype: 'wizard',
    lang: 'fr',
    title: 'Shadowban LinkedIn : mon compte est-il vraiment bridé ? Test en 6 questions',
    h1: 'Shadowban LinkedIn : suis-je vraiment bridé ?',
    valueLine:
      'Répondez à 6 questions au plus : les causes de portée basse classées, avec ce que LinkedIn documente et ce qui relève de l’hypothèse.',
    metaDescription:
      "Shadowban LinkedIn : restriction, outil d'automatisation, pod, lien dans le post ou simple manque de premier élan ? Un test gratuit classe les causes probables et sépare ce que LinkedIn documente du reste.",
    descriptionTitle: 'Comment le test sépare-t-il un shadowban d’une portée basse ?',
    description: [
      <>
        LinkedIn n'emploie pas le mot « shadowban » et ne propose aucun test de compte. Ce qu'il écrit, c'est que la
        visibilité d'un contenu peut être limitée, étiquetée ou retirée, et que les infractions répétées ou graves peuvent
        mener à une restriction du compte. Le test part de là : il ne cherche pas un bannissement invisible, il classe les
        causes qui expliquent une portée basse, en disant pour chacune si LinkedIn la documente ou non.
      </>,
      <>
        Le parcours change selon vos réponses. Un message de restriction raccourcit tout à deux écrans, parce qu'il n'y a plus
        rien à deviner. Sans avis de LinkedIn, le test pèse cinq indices : outils d'automatisation, engagement artificiel,
        forme de la baisse, liens dans le corps et réactions de la première heure. Exemple : une page entreprise qui met un
        lien dans presque chaque post, sans réaction dans l'heure, voit le lien classé derrière le manque de premier élan.
        Un profil personnel avec une extension d'envoi d'invitations voit l'outil passer en tête, avant tout le reste.
      </>,
      <>
        Les liens ne comptent pas partout pareil : les études varient de 13 % à 36 % de portée en moins, et les profils
        personnels y échappent presque, d'après la synthèse détaillée dans{' '}
        <Link to="/blog/mettre-un-lien-dans-un-post-linkedin?lang=fr">notre guide sur les liens dans un post</Link>. Pour les
        règles sur les extensions et les agents IA, voir{' '}
        <Link to="/blog/claude-linkedin-workflows?lang=fr">les workflows Claude qui font restreindre un compte</Link>, et pour
        les groupes d'entraide, <Link to="/blog/linkedin-penalise-pod-engagement?lang=fr">ce que LinkedIn dit des pods</Link>.
      </>,
      <>
        Dans la grande majorité des cas, la réponse est moins dramatique : un post sans réaction dans l'heure s'arrête à son
        premier échantillon. Si c'est votre cas, le{' '}
        <Link to="/tools/pourquoi-post-linkedin-pas-de-vues">diagnostic « pourquoi mon post n'a pas de vues »</Link> creuse
        l'horaire et la taille du réseau, et <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure d'un post</Link>{' '}
        explique le mécanisme.
      </>,
    ],
    faq: [
      {
        q: 'Le shadowban LinkedIn existe-t-il vraiment ?',
        a: "LinkedIn n'utilise pas ce mot. Il documente qu'il peut limiter la visibilité d'un contenu, l'étiqueter ou le retirer, et restreindre un compte en cas d'infractions répétées ou graves. Une limitation de visibilité ne s'accompagne pas forcément d'une notification, c'est pourquoi le soupçon est si répandu.",
      },
      {
        q: 'Comment savoir si mon compte LinkedIn est restreint ?',
        a: "Une restriction se voit : LinkedIn l'indique à la connexion ou par email, et demande de suivre des consignes à l'écran, soit un réexamen pour un motif de contenu, soit une confirmation d'identité. Si vous n'avez rien reçu de tel, votre compte n'est pas restreint au sens de l'aide LinkedIn.",
      },
      {
        q: 'Une extension ou un agent IA peut-il me faire restreindre ?',
        a: "C'est la cause que LinkedIn nomme le plus clairement : sa page d'aide interdit les logiciels, robots et extensions qui extraient des données, modifient le site ou automatisent l'activité. La sanction va de la restriction temporaire à la restriction définitive. LinkedIn ne dit pas comment il repère ces outils.",
      },
      {
        q: 'Un lien dans le post fait-il baisser la portée ?',
        a: "Souvent un peu, parfois beaucoup. Une étude LinkPost sur 358 000 posts mesure 448 impressions médianes avec lien contre 705 sans, soit 36 % d'écart ; d'autres études donnent 13 % à 26 %. Les pages entreprise encaissent l'essentiel, les profils personnels presque rien.",
      },
      {
        q: 'Combien de temps dure un shadowban ?',
        a: "Aucune durée n'est publiée, ni pour une restriction ni pour une limitation de visibilité. La page d'aide indique seulement qu'une restriction peut être temporaire ou définitive selon le type de violation. Toute durée annoncée ailleurs est une estimation.",
      },
      {
        q: 'Ce test accède-t-il à mon compte ?',
        a: "Non. Il tourne dans votre navigateur et ne voit que vos réponses. Il ne peut donc pas confirmer une restriction : il classe des hypothèses. Pour mesurer une baisse, comparez les impressions de vos derniers posts dans leurs statistiques.",
      },
    ],
    Widget: ShadowbanWizard,
  },
  {
    slug: 'taille-carrousel-linkedin',
    archetype: 'analyzer',
    lang: 'fr',
    title: 'Taille de carrousel LinkedIn : contrôle du format, du poids et de la lisibilité',
    h1: 'Taille de carrousel LinkedIn : contrôlez votre document',
    valueLine:
      'Saisissez vos pages, votre poids et vos dimensions : le bilan sépare les règles de LinkedIn des repères de lisibilité, avec une correction pour chaque point.',
    metaDescription:
      "Taille de carrousel LinkedIn : contrôlez nombre de pages (300 max), poids (100 Mo), format 4:5 ou carré, corps de texte lisible sur téléphone et densité page par page. Gratuit, sans compte.",
    descriptionTitle: 'Que contrôle-t-on avant de publier un carrousel LinkedIn ?',
    description: [
      <>
        Un carrousel LinkedIn est un document : PDF, PPT, PPTX, DOC ou DOCX. L'aide de LinkedIn fixe quatre règles
        vérifiables : 100 Mo et 300 pages au maximum, des pages de même taille, un PDF aplati, et un titre. Elle ne dit rien
        du format d'une page, du nombre de pages conseillé ni de la taille du texte. Cet outil contrôle les deux familles
        et étiquette chaque résultat « Règle LinkedIn » ou « Repère NeverZero », pour que vous sachiez ce qui bloque
        vraiment. Rien n'est envoyé à un serveur.
      </>,
      <>
        Le calcul le plus utile est celui du texte. Sur un téléphone, une page est affichée sur environ 360 px de large : un
        texte de 28 px dans une page de 1 920 px de large y paraît à 5,3 px, illisible, alors que le même corps dans une page
        de 1 080 px donne 9,3 px. Pour atteindre 11 px à l'écran, il faut 33 px à 1 080 de large, et 59 px à 1 920. C'est
        pourquoi le paysage 16:9 des présentations donne de mauvais carrousels. Ces deux chiffres (360 et 11 px) sont des
        repères NeverZero, pas des mesures de LinkedIn.
      </>,
      <>
        Si vous collez le texte des pages, l'outil lit aussi la couverture (6 à 12 mots), la densité (60 mots au plus par page)
        et la dernière page, qui doit demander une action. Exemple : une page de 95 mots est signalée comme un paragraphe à
        scinder, alors que 30 mots passent. Pour les limites des autres formats, voir notre guide sur{' '}
        <Link to="/blog/taille-post-linkedin?lang=fr">la taille d'un post LinkedIn</Link>.
      </>,
      <>
        Un carrousel bien formaté est lisible, il n'est pas pour autant diffusé : tout se joue dans{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure</Link>. Le texte qui accompagne le document
        compte aussi : testez-le avec l'<Link to="/tools/accroche-post-linkedin">analyseur d'accroche</Link> et le{' '}
        <Link to="/tools/simulateur-post-linkedin">simulateur de post</Link>, puis comparez vos résultats avec{' '}
        <Link to="/blog/taux-engagement-linkedin?lang=fr">les repères d'engagement par format</Link>.
      </>,
    ],
    faq: [
      {
        q: "Quelle est la taille d'un carrousel LinkedIn ?",
        a: "LinkedIn ne publie aucune dimension pour les pages d'un document. Le format le plus utilisé est le portrait 4:5 (1 080 × 1 350 px), suivi du carré (1 080 × 1 080 px) ; c'est un repère courant, pas une règle. Le seul format imposé est que toutes les pages aient la même taille.",
      },
      {
        q: 'Combien de pages et de Mo pour un carrousel LinkedIn ?',
        a: "300 pages et 100 Mo au maximum, selon l'aide officielle de LinkedIn. Dans la pratique, un carrousel se lit sur 5 à 15 pages et pèse bien moins de 25 Mo.",
      },
      {
        q: 'Quels fichiers LinkedIn accepte-t-il pour un carrousel ?',
        a: "PDF, PPT, PPTX, DOC et DOCX. LinkedIn conseille le PDF pour la meilleure qualité. Les vidéos et animations d'un document s'affichent en image fixe.",
      },
      {
        q: 'Pourquoi mon PDF est-il refusé ou déformé ?',
        a: "Les deux causes documentées par LinkedIn : des pages de tailles différentes, et un PDF à calques multiples qui n'a pas été aplati. Exportez en PDF aplati avec un format de page unique.",
      },
      {
        q: 'Quelle taille de police pour un carrousel LinkedIn ?',
        a: "LinkedIn n'en donne pas. Sur un téléphone, la page est réduite à environ 360 px de large ; pour garder 11 px à l'écran, comptez 33 px minimum sur une page de 1 080 px de large et 59 px sur une page de 1 920 px. Ce sont des repères NeverZero.",
      },
      {
        q: 'Mon document est-il envoyé quelque part ?',
        a: "Non. Vous saisissez des chiffres et, si vous le souhaitez, du texte : tout est calculé dans votre navigateur, rien n'est transmis ni enregistré, et aucun fichier n'est lu.",
      },
    ],
    Widget: CarouselChecker,
  },
  {
    slug: 'taille-video-linkedin',
    archetype: 'analyzer',
    lang: 'fr',
    title: 'Taille de vidéo LinkedIn : contrôle de la durée, du poids, du ratio et du format',
    h1: 'Taille de vidéo LinkedIn : contrôlez votre fichier',
    valueLine:
      'Saisissez la durée, les dimensions et le poids de votre vidéo : un verdict par critère, avec la correction, selon que vous publiez depuis un profil ou une Page.',
    metaDescription:
      "Taille de vidéo LinkedIn : contrôlez durée (15 min sur un profil, 10 sur une Page), poids (5 Go), ratio 1:2,4 à 2,4:1, définition, débit et format de fichier avant d'envoyer. Gratuit, sans compte.",
    descriptionTitle: 'Que vérifie-t-on avant d\'envoyer une vidéo sur LinkedIn ?',
    description: [
      <>
        LinkedIn publie deux séries de limites, et elles ne sont pas identiques. Pour un profil, l'aide fixe de 3 secondes
        (2 depuis l'appli mobile) à 15 minutes, de 75 Ko à 5 Go, de 256 × 144 à 4 096 × 2 304 pixels, un ratio de 1:2,4 à
        2,4:1, 10 à 60 images par seconde et 192 kbit/s à 30 Mbit/s. Pour une Page, la durée maximale tombe à 10 minutes, et
        l'aide écrit que les fichiers AVI, QuickTime et MOV ne sont plus pris en charge. L'outil applique la bonne série
        selon votre choix et étiquette chaque résultat « Règle LinkedIn » ou « Repère NeverZero ». Rien n'est envoyé à un
        serveur, et aucun fichier n'est lu : vous saisissez des chiffres.
      </>,
      <>
        Le contrôle le plus utile est le recoupement du poids et de la durée. Si vous ne connaissez pas le débit, l'outil le
        déduit : une vidéo de 4,1 Go qui dure 12 min 30 s tourne à environ 47 Mbit/s, au-dessus du plafond de 30. Le fichier
        n'est pas trop long, il est trop lourd pour sa durée. À 30 Mbit/s, le maximum autorisé, 15 minutes pèsent environ
        3,3 Go : la limite de 5 Go n'est jamais atteinte par un fichier dont le débit est conforme.
      </>,
      <>
        Deux limites méritent de la prudence. L'aide destinée aux membres ne cite aucun format de fichier : MP4 en H.264 est
        le choix le plus sûr, mais ce n'est pas écrit. Et la définition maximale est donnée comme « 4 096 × 2 304 » sans
        orientation : un 9:16 en 2 304 × 4 096 est signalé, parce que l'aide ne dit pas si elle se lit en vertical.
        Exportez en 1 080 × 1 920 et la question ne se pose plus. Le détail de chaque limite est dans notre guide sur le{' '}
        <Link to="/blog/format-video-linkedin?lang=fr">format vidéo LinkedIn</Link>.
      </>,
      <>
        Une vidéo conforme gagne seulement le droit d'être regardée. Les sous-titres et l'ouverture sont donc contrôlés comme
        des repères NeverZero, pas comme des règles : une bonne ouverture donne l'idée dans les trois premières secondes, et
        la diffusion se décide dans <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure</Link>. Pour un
        document plutôt qu'une vidéo, utilisez le <Link to="/tools/taille-carrousel-linkedin">contrôle de carrousel</Link>,
        puis testez la première phrase de votre texte avec l'<Link to="/tools/accroche-post-linkedin">analyseur d'accroche</Link>.
        Les limites des autres formats sont dans <Link to="/blog/taille-post-linkedin?lang=fr">la taille d'un post LinkedIn</Link>.
      </>,
    ],
    faq: [
      {
        q: "Quelle est la durée maximale d'une vidéo LinkedIn ?",
        a: "15 minutes pour une vidéo partagée depuis un profil, 10 minutes sur une Page, selon les deux pages d'aide de LinkedIn. Le minimum est de 3 secondes depuis un ordinateur et de 2 secondes depuis l'appli mobile.",
      },
      {
        q: "Quel poids maximal pour une vidéo LinkedIn ?",
        a: "5 Go au maximum et 75 Ko au minimum. Dans la pratique, un fichier qui respecte le débit maximal de 30 Mbit/s reste sous 3,3 Go pour 15 minutes : si le vôtre dépasse 5 Go, c'est son débit qu'il faut baisser.",
      },
      {
        q: 'Quel ratio et quelles dimensions pour une vidéo LinkedIn ?',
        a: "Un ratio de 1:2,4 (très vertical) à 2,4:1 (très large), et une définition de 256 × 144 à 4 096 × 2 304 pixels. 16:9, 1:1, 4:5 et 9:16 passent tous. LinkedIn ne recommande aucun ratio précis.",
      },
      {
        q: 'Quel format de fichier LinkedIn accepte-t-il ?',
        a: "L'aide destinée aux membres n'en cite aucun. Celle des Pages liste notamment MP4, MPEG-4, MKV et WebM, et précise que les fichiers AVI, QuickTime et MOV ne sont plus pris en charge. Exportez en MP4 (H.264) pour ne prendre aucun risque.",
      },
      {
        q: 'Quelle fréquence et quel débit pour une vidéo LinkedIn ?',
        a: "De 10 à 60 images par seconde et de 192 kbit/s à 30 Mbit/s. Pour une vidéo de parole en 1080p, 8 à 12 Mbit/s est un repère courant : c'est un ordre de grandeur NeverZero, pas une consigne de LinkedIn.",
      },
      {
        q: 'Mon fichier est-il envoyé quelque part ?',
        a: "Non. Vous saisissez des chiffres lus dans les propriétés du fichier : tout est calculé dans votre navigateur, rien n'est transmis ni enregistré, et le fichier lui-même n'est jamais lu.",
      },
    ],
    Widget: VideoChecker,
  },
  {
    slug: 'idees-post-linkedin',
    archetype: 'directory',
    lang: 'fr',
    title: 'Idées de post LinkedIn : trouvez l\'angle selon votre rôle, votre objectif et votre preuve',
    h1: 'Idées de post LinkedIn',
    valueLine:
      'Dites qui vous êtes, ce que vous visez et ce que vous avez en main : 28 angles filtrés, chacun avec son accroche, sa structure et son piège.',
    metaDescription:
      "Idées de post LinkedIn gratuites : choisissez votre rôle, votre objectif et votre preuve (résultat, erreur, méthode, opinion) pour obtenir des angles avec accroche à compléter, structure en étapes et piège à éviter.",
    descriptionTitle: 'Comment l\'outil choisit-il les angles de post ?',
    description: [
      <>
        Une liste de 30 idées vous laisse devant le même problème qu'avant : laquelle est la vôtre ? Ici, le filtre part de
        ce que vous pouvez réellement prouver. Un angle « avant / après chiffré » n'apparaît pas si vous n'avez aucun résultat
        à citer. L'objectif resserre ensuite la liste, et le rôle sert à classer les angles et à signaler ceux qu'il faut
        adapter. Rien n'est envoyé à un serveur.
      </>,
      <>
        Exemple : un consultant qui veut trouver des clients et qui détient une méthode obtient trois angles, dont « La
        première question que je pose » et « Ma méthode en N étapes ». Un recruteur qui veut recruter et qui a une erreur à
        raconter obtient « Ma pire erreur de recrutement », avec un avertissement : on raconte le processus, pas la personne
        recrutée. Un salarié qui cherche des clients voit s'afficher un rappel sur son employeur avant le moindre angle.
      </>,
      <>
        Ces angles et ces pièges sont des repères éditoriaux NeverZero, pas des règles de LinkedIn, et aucun ne garantit de
        portée. Un bon angle ne remplace pas le mécanisme de diffusion : tout se joue d'abord dans{' '}
        <Link to="/blog/premiere-heure-post-linkedin?lang=fr">la première heure d'un post</Link>.
      </>,
      <>
        Une fois l'angle choisi, passez la première phrase à l'<Link to="/tools/accroche-post-linkedin">analyseur d'accroche</Link>{' '}
        et le brouillon complet au <Link to="/tools/simulateur-post-linkedin">simulateur de post</Link>. Si un post déjà publié
        n'a rien donné, <Link to="/blog/post-linkedin-sans-engagement?lang=fr">ce guide sur un post sans engagement</Link>{' '}
        aide à comprendre pourquoi.
      </>,
    ],
    faq: [
      {
        q: 'Comment trouver des idées de post LinkedIn quand on manque de temps ?',
        a: "Partez de ce que vous avez déjà : un résultat récent, une erreur dont vous avez tiré une règle, une méthode que vous répétez, un avis que vous défendez. Un post sur l'une de ces quatre preuves se prépare plus vite qu'un sujet cherché dans l'air du temps.",
      },
      {
        q: 'Combien de posts par semaine faut-il publier ?',
        a: "LinkedIn ne donne pas de chiffre. Mieux vaut un rythme que vous tenez des mois qu'un sprint de deux semaines : un angle solide par semaine vaut plus que cinq posts de remplissage.",
      },
      {
        q: 'Puis-je publier un résultat client ?',
        a: "Seulement avec l'accord du client, ou en l'anonymisant au point qu'on ne puisse pas le reconnaître (secteur et taille, jamais le nom). Si votre contrat contient une clause de confidentialité, elle prime.",
      },
      {
        q: 'Que faire si je n\'ai aucune preuve à citer ?',
        a: "Choisissez « Une opinion » : un avis argumenté, appuyé sur ce que vous observez dans votre métier, ne demande ni chiffre ni client. L'angle « Le contre-pied argumenté » est le plus polyvalent des 28.",
      },
      {
        q: 'Les accroches proposées sont-elles à copier telles quelles ?',
        a: "Non. Les crochets sont à remplir avec vos faits, et un angle dont toutes les cases sont vides ne prouve rien. Complétez l'accroche, puis vérifiez-la avec l'analyseur d'accroche.",
      },
      {
        q: 'Mon sujet est-il envoyé quelque part ?',
        a: "Non. L'outil tourne dans votre navigateur : le sujet que vous saisissez sert uniquement à compléter les accroches affichées, rien n'est transmis ni enregistré.",
      },
    ],
    Widget: PostIdeas,
  },
];

export function listTools(lang?: Lang): ToolEntry[] {
  return lang ? TOOLS.filter((tool) => tool.lang === lang) : TOOLS;
}

export function findTool(slug: string): ToolEntry | undefined {
  return TOOLS.find((tool) => tool.slug === slug);
}
